/**
 * Vehicles API client · T-S1-A W3
 * 真契约源：apps/api-server/src/api_server/routes/vehicles.py
 */
import { api } from "./api";
import type {
  FleetSeatCalendarResponse,
  FleetSeatDayDetailResponse,
  InventoryCheckResponse,
  SeatCalendarResponse,
  SeatLayouts,
  VehiclePricingPolicy,
  VehiclePricingPolicyCreatePayload,
  VehiclePricingPolicyStatus,
  VehiclePricingPolicyUpdatePayload,
  VehicleCreatePayload,
  VehicleLicense,
  VehicleLicenseCreatePayload,
  VehicleLicenseUpdatePayload,
  VehicleListQuery,
  VehicleRead,
  VehicleStats,
  VehicleStatus,
  VehicleUpdatePayload,
} from "./types/vehicles";

function buildQuery(params: Record<string, unknown>): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    usp.set(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : "";
}

/** @param opts.paged GUARD · 调用方是带 UI 分页器的列表页时传 true（消除静默截断告警） */
export async function listVehicles(
  q: VehicleListQuery = {},
  opts?: { paged?: boolean },
): Promise<VehicleRead[]> {
  const qs = buildQuery({
    status: q.status,
    ownership_type: q.ownership_type,
    supplier_id: q.supplier_id,
    q: q.q,
    limit: q.limit,
    offset: q.offset,
  });
  return api<VehicleRead[]>(`/api/v1/vehicles${qs}`, { paged: opts?.paged });
}

export async function getVehicleStats(): Promise<VehicleStats> {
  return api<VehicleStats>("/api/v1/vehicles/_stats");
}

export async function getVehicle(id: string): Promise<VehicleRead> {
  return api<VehicleRead>(`/api/v1/vehicles/${id}`);
}

// R-03-A · canvas page-15 运营状态切换 · active / maintenance / retired
export async function setVehicleStatus(
  id: string,
  status: VehicleStatus,
  reason?: string,
): Promise<VehicleRead> {
  return api<VehicleRead>(`/api/v1/vehicles/${id}/status`, {
    method: "PATCH",
    body: JSON.stringify({ status, reason }),
  });
}

export async function createVehicle(
  payload: VehicleCreatePayload,
): Promise<VehicleRead> {
  return api<VehicleRead>("/api/v1/vehicles", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateVehicle(
  id: string,
  payload: VehicleUpdatePayload,
): Promise<VehicleRead> {
  return api<VehicleRead>(`/api/v1/vehicles/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function deleteVehicle(id: string): Promise<void> {
  await api<void>(`/api/v1/vehicles/${id}`, { method: "DELETE" });
}

// ──────────────────────────────────────────────────────────────────────────
// Licenses
// ──────────────────────────────────────────────────────────────────────────

export async function listLicenses(
  vehicleId: string,
): Promise<VehicleLicense[]> {
  return api<VehicleLicense[]>(`/api/v1/vehicles/${vehicleId}/licenses`);
}

export async function createLicense(
  vehicleId: string,
  payload: VehicleLicenseCreatePayload,
): Promise<VehicleLicense> {
  return api<VehicleLicense>(`/api/v1/vehicles/${vehicleId}/licenses`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateLicense(
  vehicleId: string,
  licenseId: string,
  payload: VehicleLicenseUpdatePayload,
): Promise<VehicleLicense> {
  return api<VehicleLicense>(`/api/v1/vehicles/${vehicleId}/licenses/${licenseId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function deleteLicense(vehicleId: string, licenseId: string): Promise<void> {
  await api<{ id: string }>(`/api/v1/vehicles/${vehicleId}/licenses/${licenseId}`, {
    method: "DELETE",
  });
}

// ──────────────────────────────────────────────────────────────────────────
// Seat calendar
// ──────────────────────────────────────────────────────────────────────────

// ──────────────────────────────────────────────────────────────────────────
// R-03-B-4 · 车辆合约价 policy（仅 self_contract）
// ──────────────────────────────────────────────────────────────────────────
export async function listPricingPolicies(
  vehicleId: string,
  query: { status?: VehiclePricingPolicyStatus } = {},
): Promise<VehiclePricingPolicy[]> {
  const qs = query.status ? `?status=${query.status}` : "";
  return api<VehiclePricingPolicy[]>(
    `/api/v1/vehicles/${vehicleId}/pricing-policies${qs}`,
  );
}

export async function createPricingPolicy(
  vehicleId: string,
  payload: VehiclePricingPolicyCreatePayload,
): Promise<VehiclePricingPolicy> {
  return api<VehiclePricingPolicy>(
    `/api/v1/vehicles/${vehicleId}/pricing-policies`,
    { method: "POST", body: JSON.stringify(payload) },
  );
}

export async function updatePricingPolicy(
  vehicleId: string,
  pid: string,
  payload: VehiclePricingPolicyUpdatePayload,
): Promise<VehiclePricingPolicy> {
  return api<VehiclePricingPolicy>(
    `/api/v1/vehicles/${vehicleId}/pricing-policies/${pid}`,
    { method: "PATCH", body: JSON.stringify(payload) },
  );
}

export async function deletePricingPolicy(
  vehicleId: string,
  pid: string,
): Promise<{ id: string }> {
  return api<{ id: string }>(
    `/api/v1/vehicles/${vehicleId}/pricing-policies/${pid}`,
    { method: "DELETE" },
  );
}

// R-03-B-2 · canvas page-12 座位布局（多版本 JSONB）
export async function getSeatLayout(vehicleId: string): Promise<SeatLayouts> {
  return api<SeatLayouts>(`/api/v1/vehicles/${vehicleId}/seat-layout`);
}

export async function saveSeatLayout(
  vehicleId: string,
  payload: SeatLayouts,
): Promise<SeatLayouts> {
  return api<SeatLayouts>(`/api/v1/vehicles/${vehicleId}/seat-layout`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function getSeatCalendar(
  vehicleId: string,
  dateFrom: string,
  dateTo: string,
): Promise<SeatCalendarResponse> {
  return api<SeatCalendarResponse>(
    `/api/v1/vehicles/${vehicleId}/seat-calendar?from=${dateFrom}&to=${dateTo}`,
  );
}

/** GET /vehicles/_seat-calendar · 集成座位日历（page-10 全车队）*/
export async function getFleetSeatCalendar(
  dateFrom: string,
  dateTo: string,
): Promise<FleetSeatCalendarResponse> {
  return api<FleetSeatCalendarResponse>(
    `/api/v1/vehicles/_seat-calendar?date_from=${dateFrom}&date_to=${dateTo}`,
  );
}

/** GET /vehicles/_seat-calendar/day · 单日钻取（每车满员 + 占用订单）
 *  VEHVIEW · productId 传入 → 车辆列表按该产品 A2 可用池过滤 + 响应带 unassigned_pax */
export async function getFleetSeatDay(
  date: string,
  productId?: string,
): Promise<FleetSeatDayDetailResponse> {
  const q = productId ? `&product_id=${encodeURIComponent(productId)}` : "";
  return api<FleetSeatDayDetailResponse>(
    `/api/v1/vehicles/_seat-calendar/day?date=${date}${q}`,
  );
}

/** VEHVIEW Part D · 可用池预览（A2 同一口径·产品向导实时算）*/
export interface VehiclePoolPreview {
  vehicle_count: number;
  total_seats: number;
}
export async function getVehiclePoolPreview(
  models: string[],
  ids: string[],
): Promise<VehiclePoolPreview> {
  const params = [
    ...models.map((m) => `models=${encodeURIComponent(m)}`),
    ...ids.map((i) => `ids=${encodeURIComponent(i)}`),
  ].join("&");
  return api<VehiclePoolPreview>(`/api/v1/vehicles/_pool-preview${params ? `?${params}` : ""}`);
}

// ──────────────────────────────────────────────────────────────────────────
// Inventory check (5-layer engine)
// ──────────────────────────────────────────────────────────────────────────

export async function inventoryCheck(
  vehicleId: string,
  targetDate: string,
  paxCount: number,
  markupPct = 0.3,
): Promise<InventoryCheckResponse> {
  return api<InventoryCheckResponse>(
    `/api/v1/vehicles/${vehicleId}/inventory-check?date=${targetDate}&pax_count=${paxCount}&markup_pct=${markupPct}`,
  );
}
